import React, { useState, useMemo, useCallback } from "react";
import useClockChangeLogs, {
  DeviceChangeLogFields,
  FieldChangeFilter,
} from "../hooks/useClockChangeLogs";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSTextField from "../../system-design/DSTextField";
import DSBox from "../../system-design/DSBox";
import DSGrid from "../../system-design/DSGrid";
import DSTypography from "../../system-design/DSTypography";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSTable from "../../system-design/Table/DSTable";
import DSTableHead from "../../system-design/Table/DSTableHead";
import DSTableBody from "../../system-design/Table/DSTableBody";
import DSTableRow from "../../system-design/Table/DSTableRow";
import DSTableCell from "../../system-design/Table/DSTableCell";
import DSTableContainer from "../../system-design/Table/DSTableContainer";
import DSAlert from "../../system-design/DSAlert";
import { IconButton, useTheme, alpha } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DSChip from "../../system-design/DSChip";
import ClockFieldSelect, { AVAILABLE_FIELDS } from "./ClockFieldSelect";
import KlydosPickDialog from "../../Gifts/GiftCategories/components/KlydosPickDialog";
import {
  changeLogCellSx as tableCellStyles,
  changeLogHeaderCellSx as headerCellStyles,
  changeLogChipColors,
  getDefaultChangeLogFromDate,
  parseRecordLimit,
  KlydoPreviewDialog,
} from "./changeLogShared";

interface ClockChangeLogsDialogProps {
  open: boolean;
  onClose: () => void;
  clockId: string;
  clockIdf: string;
}

const ClockChangeLogsDialog: React.FC<ClockChangeLogsDialogProps> = ({
  open,
  onClose,
  clockId,
  clockIdf,
}) => {
  const theme = useTheme();
  const defaultFromDate = useMemo(() => getDefaultChangeLogFromDate(), []);
  const [limit, setLimit] = useState<number | "">(100);
  const [hasQueried, setHasQueried] = useState(false);
  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [selectedFields, setSelectedFields] = useState<
    Array<keyof DeviceChangeLogFields>
  >([]);
  const [queryParams, setQueryParams] = useState<{
    fields: FieldChangeFilter;
    limit: number;
    fromDate: string;
  }>({ fields: {}, limit: 100, fromDate: defaultFromDate });
  const [selectedKlydoId, setSelectedKlydoId] = useState<string | null>(null);
  const [highlightKlydoIds, setHighlightKlydoIds] = useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const highlightSet = useMemo(
    () => new Set(highlightKlydoIds),
    [highlightKlydoIds],
  );

  // Generic matcher: returns true when the given id is in the highlight set.
  // Kept field-agnostic so the same highlight can later be reused for other
  // string-array fields (e.g. a future "collection" log).
  const matchesSearch = useCallback(
    (value: string) => highlightSet.has(value),
    [highlightSet],
  );

  const handleFieldsChange = useCallback(
    (fields: Array<keyof DeviceChangeLogFields>) => {
      setSelectedFields(fields);
      setHasQueried(false);
    },
    [],
  );

  const handleLimitChange = useCallback((value: number | "") => {
    setLimit(value);
    setHasQueried(false);
  }, []);

  const fieldsFilter = useMemo((): FieldChangeFilter => {
    const filter: FieldChangeFilter = {};
    selectedFields?.forEach((field) => {
      filter[field] = true;
    });
    return filter;
  }, [selectedFields]);

  const { data, isLoading, error } = useClockChangeLogs(
    clockId,
    queryParams.fields,
    hasQueried,
    queryParams.limit,
    queryParams.fromDate,
  );

  const handleFetchData = useCallback(() => {
    setQueryParams({
      fields: fieldsFilter,
      limit: typeof limit === "number" ? limit : 100,
      fromDate: fromDate,
    });
    setHasQueried(true);
  }, [fieldsFilter, limit, fromDate]);

  const STRING_ARRAY_FIELDS: Array<keyof DeviceChangeLogFields> = [
    "feeds",
    "favorites",
  ];

  const renderArrayDiff = (
    current: unknown[],
    previous: unknown[] | undefined,
  ) => {
    const strings = current.filter(
      (item): item is string => typeof item === "string",
    );
    const prevStrings = (previous ?? []).filter(
      (item): item is string => typeof item === "string",
    );
    const prevSet = new Set(prevStrings);
    const currSet = new Set(strings);
    const added = strings.filter((item) => !prevSet.has(item));
    const removed = prevStrings.filter((item) => !currSet.has(item));

    const renderChip = (item: string, kind: "added" | "removed") => {
      const highlighted = matchesSearch(item);
      const dimmed = highlightSet.size > 0 && !highlighted;
      const palette = changeLogChipColors(theme, kind);
      const sign = kind === "added" ? "+" : "-";

      return (
        <DSChip
          key={`${sign}${item}`}
          label={`${sign} ${item.length > 8 ? item.slice(0, 8) + "…" : item}`}
          title={item}
          size="small"
          clickable
          onClick={() => setSelectedKlydoId(item)}
          sx={{
            ...palette,
            fontSize: "0.75rem",
            height: 24,
            cursor: "pointer",
            opacity: dimmed ? 0.3 : 1,
            fontWeight: highlighted ? 700 : 400,
            border: highlighted
              ? `2px solid ${theme.palette.info.main}`
              : "none",
            boxShadow: highlighted
              ? `0 0 0 2px ${alpha(theme.palette.info.main, 0.5)}`
              : "none",
          }}
        />
      );
    };

    return (
      <DSBox
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          alignItems: "center",
        }}
      >
        {added.map((item) => renderChip(item, "added"))}
        {removed.map((item) => renderChip(item, "removed"))}
        <DSTypography
          variant="caption"
          sx={{ color: "text.secondary", ml: 0.5 }}
        >
          {strings.length} total
        </DSTypography>
      </DSBox>
    );
  };

  return (
    <DSDialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DSDialogTitle>
        <DSBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <DSTypography variant="h6">
            Clock Change Logs - {clockIdf}
          </DSTypography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DSBox>
      </DSDialogTitle>

      <DSDialogContent>
        <DSGrid container spacing={2} sx={{ mb: 3 }}>
          <DSGrid item xs={12} md={4}>
            <ClockFieldSelect
              selectedFields={selectedFields}
              onFieldsChange={handleFieldsChange}
              helperText="Choose which fields to display in the logs"
            />
          </DSGrid>

          <DSGrid item xs={12} md={2}>
            <DSTextField
              fullWidth
              type="number"
              value={limit}
              onChange={(e) => {
                if (e.target.value === "") {
                  handleLimitChange("");
                  return;
                }
                const n = parseRecordLimit(e.target.value);
                if (n !== null) handleLimitChange(n);
              }}
              helperText="Number of records to fetch"
            />
          </DSGrid>

          <DSGrid item xs={12} md={2}>
            <DSTextField
              fullWidth
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setHasQueried(false);
              }}
              helperText="Query data from this date"
            />
          </DSGrid>

          <DSGrid item xs={12} md={2}>
            <DSPrimaryButton
              fullWidth
              onClick={handleFetchData}
              disabled={isLoading}
              sx={{ height: "56px" }}
            >
              {isLoading ? "Fetching..." : "Fetch Data"}
            </DSPrimaryButton>
          </DSGrid>
        </DSGrid>

        {hasQueried &&
          !isLoading &&
          selectedFields.some((f) => STRING_ARRAY_FIELDS.includes(f)) && (
            <DSBox
              sx={{
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <DSPrimaryButton onClick={() => setPickerOpen(true)}>
                {highlightKlydoIds.length > 0
                  ? `Highlighting ${highlightKlydoIds.length} klydo${
                      highlightKlydoIds.length > 1 ? "s" : ""
                    } — edit`
                  : "Pick klydos to highlight"}
              </DSPrimaryButton>
              {highlightKlydoIds.length > 0 && (
                <DSSecondaryButton onClick={() => setHighlightKlydoIds([])}>
                  Clear
                </DSSecondaryButton>
              )}
              <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
                Highlights the chosen klydos in the feed / favorites changelog
              </DSTypography>
            </DSBox>
          )}

        {isLoading && (
          <DSBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={5}
          >
            <DSCircularProgress />
            <DSTypography variant="body1" sx={{ mt: 2 }}>
              Loading clock change logs...
            </DSTypography>
          </DSBox>
        )}

        {error && (
          <DSAlert severity="error" sx={{ mb: 2 }}>
            Error loading data: {error.message}
          </DSAlert>
        )}

        {!isLoading && hasQueried && !error && (
          <DSTableContainer
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 0,
            }}
          >
            <DSTable size="small" sx={{ tableLayout: "auto" }}>
              <DSTableHead>
                <DSTableRow>
                  <DSTableCell
                    sx={{
                      ...headerCellStyles,
                      minWidth: 60,
                      position: "sticky",
                      left: 0,
                      zIndex: 2,
                    }}
                  >
                    Row
                  </DSTableCell>
                  <DSTableCell sx={{ ...headerCellStyles, minWidth: 200 }}>
                    Timestamp
                  </DSTableCell>
                  {selectedFields?.map((field) => {
                    const fieldConfig = AVAILABLE_FIELDS.find(
                      (f) => f.value === field,
                    );
                    return (
                      <DSTableCell
                        key={field}
                        sx={{
                          ...headerCellStyles,
                          minWidth: 300,
                        }}
                      >
                        {fieldConfig?.label || field}
                      </DSTableCell>
                    );
                  })}
                </DSTableRow>
              </DSTableHead>
              <DSTableBody>
                {data?.length === 0 ? (
                  <DSTableRow>
                    <DSTableCell
                      colSpan={selectedFields?.length + 2}
                      align="center"
                    >
                      <DSTypography variant="body2" color="textSecondary">
                        No data found
                      </DSTypography>
                    </DSTableCell>
                  </DSTableRow>
                ) : (
                  data?.map((log, index) => (
                    <DSTableRow
                      key={index}
                      sx={{
                        "&:nth-of-type(even)": {
                          backgroundColor: (theme) =>
                            theme.palette.action.hover,
                        },
                        "&:hover": {
                          backgroundColor: theme.palette.action.selected,
                        },
                      }}
                    >
                      <DSTableCell
                        sx={{
                          ...tableCellStyles,
                          position: "sticky",
                          left: 0,
                          backgroundColor: "inherit",
                          zIndex: 1,
                          textAlign: "center",
                        }}
                      >
                        {index + 1}
                      </DSTableCell>
                      <DSTableCell
                        sx={{ ...tableCellStyles, whiteSpace: "nowrap" }}
                      >
                        {log.timestamp ? String(log.timestamp) : "-"}
                      </DSTableCell>
                      {selectedFields?.map((field) => {
                        const value = log[field];
                        const isStringArray =
                          STRING_ARRAY_FIELDS.includes(field) &&
                          Array.isArray(value);
                        const previousLog = data?.[index + 1];
                        const previousValue = previousLog?.[field];

                        return (
                          <DSTableCell
                            key={field}
                            sx={{
                              ...tableCellStyles,
                              maxWidth: 600,
                              wordBreak: "break-word",
                              whiteSpace: "pre-wrap",
                              overflow: "hidden",
                            }}
                          >
                            {isStringArray
                              ? renderArrayDiff(
                                  value as unknown[],
                                  Array.isArray(previousValue)
                                    ? (previousValue as unknown[])
                                    : undefined,
                                )
                              : JSON.stringify(value ?? "-")}
                          </DSTableCell>
                        );
                      })}
                    </DSTableRow>
                  ))
                )}
              </DSTableBody>
            </DSTable>
          </DSTableContainer>
        )}

        {!hasQueried && !isLoading && (
          <DSBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={5}
          >
            <DSTypography variant="body1" color="textSecondary">
              Select fields and click "Fetch Data" to load clock change logs
            </DSTypography>
          </DSBox>
        )}
        {pickerOpen && (
          <KlydosPickDialog
            selectedKlydos={highlightKlydoIds}
            onPickKlydos={setHighlightKlydoIds}
            onClose={() => setPickerOpen(false)}
          />
        )}
        {selectedKlydoId && (
          <KlydoPreviewDialog
            klydoId={selectedKlydoId}
            onClose={() => setSelectedKlydoId(null)}
          />
        )}
      </DSDialogContent>
    </DSDialog>
  );
};

export default ClockChangeLogsDialog;
